/**
 * Steam Price Helper
 * Utilities for fetching Steam game prices based on user's region
 */

// Map of common countries to Steam store country codes
const COUNTRY_TO_STEAM_CODE = {
  'US': 'us', 'CA': 'ca', 'GB': 'uk', 'DE': 'de', 'FR': 'fr',
  'ES': 'es', 'IT': 'it', 'RU': 'ru', 'BR': 'br', 'AU': 'au',
  'NZ': 'nz', 'JP': 'jp', 'KR': 'kr', 'CN': 'cn', 'IN': 'in',
  'MX': 'mx', 'AR': 'ar', 'CL': 'cl', 'PL': 'pl', 'TR': 'tr',
  'SE': 'se', 'NO': 'no', 'DK': 'dk', 'FI': 'fi', 'ZA': 'za',
  'SG': 'sg', 'MY': 'my', 'TH': 'th', 'ID': 'id', 'PH': 'ph',
  'VN': 'vn', 'AE': 'ae', 'SA': 'sa', 'IL': 'il', 'EG': 'eg',
  'NG': 'ng', 'KE': 'ke', 'GH': 'gh', 'TZ': 'tz', 'UG': 'ug'
};

// Currency symbols for display
const CURRENCY_SYMBOLS = {
  'USD': '$', 'EUR': '€', 'GBP': '£', 'JPY': '¥', 'CNY': '¥',
  'INR': '₹', 'RUB': '₽', 'BRL': 'R$', 'AUD': 'A$', 'CAD': 'C$',
  'MXN': 'MX$', 'KRW': '₩', 'TRY': '₺', 'ARS': 'AR$', 'CLP': 'CL$',
  'PLN': 'zł', 'SEK': 'kr', 'NOK': 'kr', 'DKK': 'kr', 'ZAR': 'R',
  'SGD': 'S$', 'MYR': 'RM', 'THB': '฿', 'IDR': 'Rp', 'PHP': '₱',
  'VND': '₫', 'AED': 'د.إ', 'SAR': '﷼', 'ILS': '₪', 'EGP': 'E£',
  'NGN': '₦', 'KES': 'KSh', 'GHS': 'GH₵', 'TZS': 'TSh', 'UGX': 'USh'
};

/**
 * Get user's country code from IP geolocation
 * Uses a free IP geolocation service
 */
export const getUserCountryCode = async () => {
  try {
    // Try to get from localStorage cache first (valid for 24 hours)
    const cached = localStorage.getItem('userCountryCode');
    const cacheTime = localStorage.getItem('countryCodeCacheTime');
    
    if (cached && cacheTime) {
      const cacheAge = Date.now() - parseInt(cacheTime);
      if (cacheAge < 24 * 60 * 60 * 1000) { // 24 hours
        return cached;
      }
    }

    // Fetch from IP geolocation API (free tier)
    const response = await fetch('https://ipapi.co/json/');
    const data = await response.json();
    
    const countryCode = data.country_code || 'US'; // Default to US
    
    // Cache the result
    localStorage.setItem('userCountryCode', countryCode);
    localStorage.setItem('countryCodeCacheTime', Date.now().toString());
    
    return countryCode;
  } catch (error) {
    console.error('Error fetching country code:', error);
    return 'US'; // Default to US on error
  }
};

/**
 * Get Steam store country code from ISO country code
 */
export const getSteamCountryCode = (isoCountryCode) => {
  return COUNTRY_TO_STEAM_CODE[isoCountryCode] || 'us';
};

/**
 * Fetch Steam game price for a specific app ID and country
 * Uses our backend proxy to avoid CORS issues
 */
export const fetchSteamPrice = async (appId, countryCode = 'us') => {
  try {
    const steamCode = getSteamCountryCode(countryCode);
    
    // Use our backend proxy endpoint instead of calling Steam directly
    const response = await fetch(
      `/api/discounts/steam/price?appId=${appId}&cc=${steamCode}`
    );
    
    if (!response.ok) {
      console.error('Steam price API error:', response.status, await response.text());
      return null;
    }
    
    const data = await response.json();
    const priceData = data?.price_overview;
    
    if (!priceData) {
      return {
        isFree: true,
        currency: 'USD',
        formattedPrice: 'Free'
      };
    }
    
    return {
      isFree: false,
      currency: priceData.currency,
      initialPrice: priceData.initial / 100, // Convert from cents
      finalPrice: priceData.final / 100,
      discountPercent: priceData.discount_percent,
      formattedInitial: priceData.initial_formatted,
      formattedFinal: priceData.final_formatted
    };
  } catch (error) {
    console.error('Error fetching Steam price:', error);
    return null;
  }
};

/**
 * Format price with currency symbol
 */
export const formatPrice = (amount, currency) => {
  const symbol = CURRENCY_SYMBOLS[currency] || currency;
  
  // For currencies that typically don't use decimals
  const noDecimalCurrencies = ['JPY', 'KRW', 'VND', 'IDR'];
  
  if (noDecimalCurrencies.includes(currency)) {
    return `${symbol}${Math.round(amount)}`;
  }
  
  return `${symbol}${amount.toFixed(2)}`;
};

/**
 * Get price information for display in the UI
 * Returns formatted price data ready for rendering
 */
export const getPriceDisplay = async (steamAppId) => {
  try {
    // Get user's country
    const countryCode = await getUserCountryCode();
    
    // Fetch price data
    const priceData = await fetchSteamPrice(steamAppId, countryCode);
    
    if (!priceData) {
      return null;
    }
    
    if (priceData.isFree) {
      return {
        isFree: true,
        displayPrice: 'Free to Play',
        currency: 'USD'
      };
    }
    
    // Check if there's a Steam discount
    const hasSteamDiscount = priceData.discountPercent > 0;
    
    return {
      isFree: false,
      currency: priceData.currency,
      currentPrice: priceData.finalPrice,
      originalPrice: hasSteamDiscount ? priceData.initialPrice : null,
      steamDiscountPercent: priceData.discountPercent,
      formattedCurrentPrice: priceData.formattedFinal,
      formattedOriginalPrice: hasSteamDiscount ? priceData.formattedInitial : null,
      hasSteamDiscount
    };
  } catch (error) {
    console.error('Error getting price display:', error);
    return null;
  }
};
